import React, { useState, ChangeEvent, useEffect } from 'react';
import { ModalWithButtons } from '../../ModalWithButtons/ModalWithButtons.component';
import { Grid, TextField, FormHelperTextProps, Typography, FormControlLabel } from '@material-ui/core';
import { LeavePrompt } from '../../Prompt/LeavePrompt/LeavePrompt.component';
import { useStyles } from './ContextualHelpList.styles';
import { useLayoutContext } from '../../../Context/Layout.context';
import { CheckboxOne } from '../../CheckboxOne/CheckboxOne.component';
import { ValidationRow } from '../../ValidationRow/ValidationRow.components';
import { updateContextualHelp } from '../../../services/contextualHelp/contextualHelp.service';
import { ContextualHelpDetails, ContextualHelpResult } from '../../../services/contextualHelp/contextualHelp.types';
import { useTranslation } from 'react-i18next';

const TITLE_MAX_LENGTH = 256;
const DESCRIPTION_MAX_LENGTH = 256;
const URL_MAX_LENGTH = 1024;

interface EditContextualHelpProps {
  data: ContextualHelpDetails;
  title: string;
  onCancel: () => void;
  onClose: () => void;
}

export const EditContextualHelp: React.FC<EditContextualHelpProps> = ({ data, title, onCancel, onClose }) => {
  const classes = useStyles();

  const { showLoader, openAlert } = useLayoutContext();
  const { t } = useTranslation(); 

  const [isLeavePromptOpen, setIsLeavePromptOpen] = useState<boolean>(false);
  const [contextualHelpTitle, setContextualHelpTitle] = useState<string>(data.title ?? '');
  const [contextualHelpDescription, setContextualHelpDescription] = useState<string>(data.description ?? '');
  const [contextualHelpUrl, setContextualHelpUrl] = useState<string>(data.url ?? '');

  const [hasTitleFocus, setHasTitleFocus] = useState<boolean>(false);
  const [hasDescriptionFocus, setHasDescriptionFocus] = useState<boolean>(false);
  const [hasUrlFocus, setHasUrlFocus] = useState<boolean>(false);
  const [isDataChanged, setIsDataChanged] = useState<boolean>(true);

  const contextualHelpArea = data.area ?? '';
  const contextualHelpSection = data.section ?? '';

  const isContextualHelpTitleToLong = contextualHelpTitle.length > TITLE_MAX_LENGTH;
  const isContextualHelpDescriptionToLong = contextualHelpDescription.length > DESCRIPTION_MAX_LENGTH;
  const isContextualHelpURLToLong = contextualHelpUrl.length > URL_MAX_LENGTH;

  const handleSuccess = () => {
    openAlert('success');
    onClose();
  };

  const handleError = (error: { response: { data: ContextualHelpResult } }) => {
    openAlert('error');
  };

  const handleUpdateContextualHelp = () => {
    showLoader(true);
    updateContextualHelp(
      data.id,
      contextualHelpTitle,
      contextualHelpDescription,
      contextualHelpUrl
    )
      .then(
        () => {
          handleSuccess();
        },
        (error) => {
          handleError(error);
        },
      )
      .finally(() => {
        showLoader(false);
      });
  };

  const handleContextualHelpTitleChange = (value: string) => {
    setContextualHelpTitle(value);
  };

  const handleContextualHelpDescriptionChange = (value: string) => {
    setContextualHelpDescription(value);
  };


  const handleContextualHelpUrlChange = (value: string) => {
    setContextualHelpUrl(value);
  };

  useEffect(() => {
    setIsDataChanged((contextualHelpTitle !== '' && contextualHelpTitle !== data.title) ||
    (contextualHelpDescription !== '' && contextualHelpDescription !== data.description) ||
    (contextualHelpUrl !== data.url))
  }, [
    contextualHelpTitle,
    contextualHelpDescription,
    contextualHelpUrl,
    data
  ]);

  const handleCancel = () => {
    isDataChanged ? setIsLeavePromptOpen(true) : onCancel();
  };

  return (
    <>
      <ModalWithButtons
        xCloseButton={handleCancel}
        open={true}
        size="sm"
        title={title}
        autoHeight
        onCancel={handleCancel}
        confirmButtonLabel="Zapisz"
        onConfirm={() => handleUpdateContextualHelp()}
        confirmButtonDisabled={
          !contextualHelpTitle ||
          !contextualHelpDescription ||
          isContextualHelpTitleToLong ||
          isContextualHelpDescriptionToLong ||
          isContextualHelpURLToLong ||
          !isDataChanged
        }
        buttonsContainerStyle={{ marginTop: '20px' }}
        handleClose={() => setIsLeavePromptOpen(true)}
      >
        <Grid container>
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>Tytuł</Typography>
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={contextualHelpTitle}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleContextualHelpTitleChange(e.target.value)}
              label="Tytuł pomocy kontekstowej*"
              variant="outlined"
              fullWidth
              inputProps={{
                style: {
                  color: isContextualHelpTitleToLong ? 'red' : 'black',
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
              onFocus={() => setHasTitleFocus(true)}
              onBlur={() => setHasTitleFocus(false)}
              error={isContextualHelpTitleToLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={
                    isContextualHelpTitleToLong
                      ? 'Wprowadzona nazwa jest za długa'
                      : ''
                  }
                  limitsVisible={hasTitleFocus || isContextualHelpTitleToLong}
                  valueLength={contextualHelpTitle.length}
                  limit={TITLE_MAX_LENGTH}
                  withError={isContextualHelpTitleToLong}
                />
              }
            />
          </Grid>
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>Miejsce w systemie {t('global:shortNames.system')}</Typography>
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={contextualHelpArea}
              label="Obszar"
              variant="outlined"
              fullWidth
              disabled
            />
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={contextualHelpSection}
              label="Sekcja"
              variant="outlined"
              fullWidth
              disabled
            />
          </Grid>
          <Grid xs={12} className={classes.helperRow}></Grid>
          <Grid item xs={12}>
            <Typography className={classes.subTitle}>Opis i link</Typography>
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={contextualHelpDescription}
              multiline
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleContextualHelpDescriptionChange(e.target.value)}
              label="Opis*"
              variant="outlined"
              fullWidth
              inputProps={{
                style: {
                  height: '100px',
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
              onFocus={() => setHasDescriptionFocus(true)}
              onBlur={() => setHasDescriptionFocus(false)}
              error={isContextualHelpDescriptionToLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={isContextualHelpDescriptionToLong ? 'Wprowadzony opis jest za długi' : ''}
                  limitsVisible={hasDescriptionFocus || isContextualHelpDescriptionToLong}
                  valueLength={contextualHelpDescription.length}
                  limit={DESCRIPTION_MAX_LENGTH}
                  withError={isContextualHelpDescriptionToLong}
                />
              }
            />
          </Grid>
          <Grid item xs={12} className={classes.item}>
            <TextField
              value={contextualHelpUrl}
              onChange={(e: ChangeEvent<HTMLInputElement>) => handleContextualHelpUrlChange(e.target.value)}
              label="Adres URL"
              variant="outlined"
              fullWidth
              inputProps={{
                style: {
                  color: isContextualHelpURLToLong ? 'red' : 'black',
                },
              }}
              InputLabelProps={{
                shrink: true,
              }}
              onFocus={() => setHasUrlFocus(true)}
              onBlur={() => setHasUrlFocus(false)}
              error={isContextualHelpURLToLong}
              FormHelperTextProps={
                {
                  component: 'div',
                } as Partial<FormHelperTextProps<'p'>>
              }
              helperText={
                <ValidationRow
                  helperText={
                    isContextualHelpTitleToLong
                      ? 'Wprowadzona nazwa jest za długa'
                      : ''
                  }
                  limitsVisible={hasUrlFocus || isContextualHelpURLToLong}
                  valueLength={contextualHelpUrl.length}
                  limit={URL_MAX_LENGTH}
                  withError={isContextualHelpURLToLong}
                />
              }
            />
          </Grid>
        </Grid>
      </ModalWithButtons>
      <LeavePrompt isOpen={isLeavePromptOpen} onConfirm={onCancel} onCancel={() => setIsLeavePromptOpen(false)} />
    </>
  );
};
